import { tourApi } from './services/api';
import { logError } from './utils/logger';
import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, Typography, Button } from '@mui/material';
import PageSkeleton from './components/ui/PageSkeleton';
import { TS } from './theme/typography';
import ErrorBoundary from './components/ErrorBoundary';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TourProvider, useTour } from './contexts/TourContext';
import TourOverlay from './components/tour/TourOverlay';
import Login from './components/Login';
// import Register from './components/Register'; // registration closed — re-enable with ALLOWED_REGISTRATION_EMAILS
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import ProtectedRoute from './components/ProtectedRoute';
import PageLayout from './components/layout/PageLayout';
// Not lazy: a redirect renders nothing, so a chunk fetch would only add a hop.
import { DiaryRedirect } from './components/routing/DiaryRedirect';
import { FieldsRedirect } from './components/routing/FieldsRedirect';
import './App.css';

const MapShell = lazy(() => import('./components/dashboard/MapShell'));
const VwcwDashboard = lazy(() => import('./components/dashboard/VwcwDashboard'));
const Weather = lazy(() => import('./components/Weather'));
// HistoryPage removed — /history redirects to /schedules (Completed tab covers same data)
const AdminAnalytics = lazy(() => import('./components/AdminAnalytics'));
// Unified admin "Hardware" surface — composes the former Sensors, EdgeDevices
// and Devices pages as tabs. The old routes redirect into it (see below).
const Hardware = lazy(() => import('./components/Hardware'));
const Alerts = lazy(() => import('./components/Alerts'));
const Reports = lazy(() => import('./components/Reports'));
const ShowcaseLauncher = lazy(() => import('./components/showcase/ShowcaseLauncher'));
const ShowcaseCinema = lazy(() => import('./components/showcase/ShowcaseCinema'));
const Analytics = lazy(() => import('./components/Analytics'));
const WeeklySchedule = lazy(() => import('./components/WeeklySchedule'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const SoilProfileEditor = lazy(() => import('./components/admin/SoilProfileEditor'));
const HistoryPage = lazy(() => import('./components/admin/HistoryPage'));
const SupportMessages = lazy(() => import('./components/SupportMessages'));
const FieldJournal = lazy(() => import('./components/FieldJournal'));
const Settings = lazy(() => import('./components/Settings'));
const DSSDemo = lazy(() => import('./components/DSSDemo'));
const WelcomePage = lazy(() => import('./components/WelcomePage'));

// 404 Not Found page
const NotFound = () => {
  const { t } = useLanguage();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '60vh', gap: 2 }}>
      <Typography variant="h1" sx={{ fontSize: '4rem', fontWeight: 700, color: 'text.secondary' }}>404</Typography>
      <Typography variant="h5" color="text.secondary">{t('pageNotFound')}</Typography>
      <Button variant="contained" href="/" sx={{ mt: 2 }}>{t('backToFields')}</Button>
    </Box>
  );
};

// DSS Intelligence route — admin-only (no env flag required)
const DssDemoRoute = () => {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/" replace />;
  return (
    <ProtectedRoute>
      <PageLayout title="dssDemo">
        <ErrorBoundary>
          <Suspense fallback={<PageSkeleton />}>
            <DSSDemo />
          </Suspense>
        </ErrorBoundary>
      </PageLayout>
    </ProtectedRoute>
  );
};

// Theme — TerraMind design system (vibrant green + brown undertones)
const theme = createTheme({
  palette: {
    primary: {
      main: '#5a8f3c',      // vibrant natural green — website brand
      light: '#7cb342',
      dark: '#3d6b28',
    },
    secondary: {
      main: '#8d6e3f',      // warm brown undertone — subtle accent
      light: '#a8896a',
      dark: '#6b5230',
    },
    success: {
      main: '#5a8f3c',
      light: '#8bc34a',
      dark: '#5a8f3c',
    },
    warning: {
      main: '#f59e0b',
      light: '#fcd34d',
      dark: '#d97706',
    },
    error: {
      main: '#dc2626',
      light: '#f87171',
      dark: '#b91c1c',
    },
    background: {
      default: '#faf8f5',   // warm cream — subtle brown undertone
      paper: '#ffffff',
    },
    text: {
      primary: '#2d2d2d',
      secondary: '#6b7280',
    },
    divider: '#e8e4de',     // warm divider — brown undertone
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    fontSize: 15,
    // Scale: import TS from theme/typography.js to use these in components
    h1: { fontSize: '2rem',    fontWeight: 700, letterSpacing: '-0.025em', color: '#111827' },
    h2: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em',  color: '#111827' },
    h3: { fontSize: '1.5rem',  fontWeight: 700, letterSpacing: '-0.015em', color: '#111827' },
    h4: { fontSize: '1.35rem', fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontSize: '1.2rem',  fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontSize: '1.05rem', fontWeight: 700, letterSpacing: '-0.01em' },
    subtitle1: { fontSize: TS.lg,   fontWeight: 600 },                         // 16px — panel/section headers
    subtitle2: { fontSize: TS.base, fontWeight: 600, color: '#6b7280' },       // 15px — sub-section headers
    body1:     { fontSize: TS.base, fontWeight: 400 },                         // 15px — main body
    body2:     { fontSize: TS.md,   fontWeight: 400 },                         // 14px — secondary text
    caption:   { fontSize: TS.sm,   fontWeight: 500, color: '#9ca3af' },       // 13px — true metadata only
    overline:  { fontSize: TS.sm,   fontWeight: 700, letterSpacing: '0.08em' },// 13px — section labels
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          border: '1px solid #f3f4f6',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
            borderColor: '#e5e7eb',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 12 },
      },
      defaultProps: { elevation: 0 },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 20px',
          fontSize: TS.base,
          letterSpacing: '-0.01em',
          minHeight: 40,       // was 36 — larger click target for farmers
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': { borderWidth: 1.5 },
        },
        sizeSmall: {
          padding: '5px 14px',
          fontSize: TS.md,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: TS.md,   // 14px — default chip
          height: 32,        // was 30 — raised to meet minHeight 32 touch target
          minHeight: 32,     // chips are data display, not primary touch targets
        },
        sizeSmall: {
          fontSize: TS.md,   // 14px — was 13px; small chips still readable
          height: 28,        // was 26 — raised to meet minHeight 28
          minHeight: 28,
        },
      },
    },
    // Form inputs — consistent size across the app
    MuiInputBase: {
      styleOverrides: {
        root: { fontSize: TS.base },
        sizeSmall: { fontSize: TS.md },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: TS.md,
          '&.Mui-focused': { color: '#5a8f3c' },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { fontSize: TS.sm, color: '#4b5563' },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: { fontSize: TS.base, fontWeight: 500 },
      },
    },
    // Navigation / menus
    MuiMenuItem: {
      styleOverrides: {
        root: { fontSize: TS.base },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary:   { fontSize: TS.base },
        secondary: { fontSize: TS.md },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: TS.base,    // 15px — was 14px, tabs must be readable
          fontWeight: 600,
          textTransform: 'none',
          minHeight: 44,        // comfortable touch/click target
        },
      },
    },
    // Tables
    MuiTableCell: {
      styleOverrides: {
        root: { fontSize: TS.md },
        head: { fontSize: TS.sm, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' },
      },
    },
    // Alerts & dialogs
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontSize: TS.base,
        },
        message: { fontSize: TS.base },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          minWidth: 'min(560px, calc(100vw - 32px))',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '1.1rem',
          padding: '20px 24px 16px',
          borderBottom: '1px solid #f3f4f6',
          letterSpacing: '-0.01em',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '20px 24px' },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 16px',
          borderTop: '1px solid #f3f4f6',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          fontSize: TS.md,     // was TS.sm (13px) — 14px is readable
          fontWeight: 500,
          padding: '8px 14px', // slightly more room
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '& fieldset': { borderColor: '#e5e7eb' },
            '&:hover fieldset': { borderColor: '#d1d5db' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#7cb342',
              borderWidth: 1.5,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#5a8f3c',
            borderWidth: 2,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 46, height: 28, padding: 0,
          // MUI's small size keeps its own thumb and travel, which did not fit
          // the track above: the thumb stopped short of the end, off centre.
          '&.MuiSwitch-sizeSmall': {
            width: 38, height: 22,
            '& .MuiSwitch-switchBase': {
              padding: 3,
              '&.Mui-checked': { transform: 'translateX(16px)' },
            },
            '& .MuiSwitch-thumb': { width: 16, height: 16 },
          },
        },
        switchBase: {
          padding: 3,
          // Override the global MuiIconButton minHeight:44 which bleeds into
          // the Switch's internal ButtonBase and misaligns the visual track.
          minHeight: 'unset',
          minWidth: 'unset',
          '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#fff',
            '& + .MuiSwitch-track': { backgroundColor: '#5a8f3c', opacity: 1 },
          },
        },
        thumb: { width: 22, height: 22 },
        track: { borderRadius: 14, backgroundColor: '#c4c4c4' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'background-color 0.15s ease',
          minWidth: 44,
          minHeight: 44,
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: { minHeight: 44 },
      },
    },
  },
});

// Auto-start tour on first login (must be inside TourProvider + AuthProvider).
// Only fires on / (and /fields/:id) — not on /welcome — so the user sees
// the welcome choice screen without an overlay interrupting it. The tour
// naturally triggers once they choose a path and land on the map.
const FirstLoginTourTrigger = () => {
  const { startTour, tourActive, inShowcase } = useTour();
  const { user } = useAuth();
  const location = useLocation();
  const isWelcome = location.pathname === '/welcome';
  // /showcase lands on the map, and a first-time browser profile -- which is
  // what a trade stand always has -- used to get the section picker thrown
  // over the demo 1.5s later. The walkthrough never played at all.
  const isShowcase = inShowcase || location.pathname === '/showcase';

  useEffect(() => {
    if (!user || tourActive || isWelcome || isShowcase) return undefined;
    const key = `terramind_tour_shown_${user.id || user.username}`;
    if (localStorage.getItem(key)) return undefined;
    // Only a trial account (one with the sample fields) gets the tour thrown
    // over its first screen. A grower with real fields opening the app in a
    // new browser -- the client on a presentation laptop -- got the section
    // picker over his own farm (walk 2026-10-03). The tour stays one click
    // away in Settings for anyone.
    let timer;
    let alive = true;
    tourApi.getStatus()
      .then(({ data }) => {
        if (!alive || !data?.active) return;
        localStorage.setItem(key, 'true');
        timer = setTimeout(() => startTour(), 1500);
      })
      .catch((e) => logError(e, 'FirstLoginTourTrigger.status'));
    return () => { alive = false; clearTimeout(timer); };
  }, [user, tourActive, startTour, isWelcome, isShowcase]);

  return null;
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <TourProvider>
            <FirstLoginTourTrigger />
            <ErrorBoundary>
              <TourOverlay />
              {/* The demo's own chrome. Renders nothing unless a showcase is
                  running, and takes the place of the tutorial tooltip while it
                  is -- same steps, same real screens, trailer voice. */}
              <Suspense fallback={null}><ShowcaseCinema /></Suspense>
              <Routes>
                <Route path="/login" element={<Login />} />
                {/* Registration closed — redirect to login. Re-enable when ALLOWED_REGISTRATION_EMAILS is set. */}
                <Route path="/register" element={<Navigate to="/login" replace />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
                <Route path="/welcome" element={<ProtectedRoute><Suspense fallback={<PageSkeleton />}><WelcomePage /></Suspense></ProtectedRoute>} />

                {/* Farmer routes */}
                <Route path="/" element={<ProtectedRoute><PageLayout title="map"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><MapShell /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                <Route path="/fields/:id" element={<ProtectedRoute><PageLayout title="map"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><MapShell /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                <Route path="/fields/:id/dashboard" element={<ProtectedRoute><PageLayout title="fieldData"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><VwcwDashboard /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                {/* The home list is the list of fields (audit step 4, M2). */}
                <Route path="/fields" element={<FieldsRedirect />} />
                <Route path="/weather" element={<ProtectedRoute><PageLayout title="weather"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><Weather /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                <Route path="/history" element={<Navigate to="/schedules" replace />} />
                <Route path="/journal" element={<ProtectedRoute><PageLayout title="fieldJournal"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><FieldJournal /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                <Route path="/diary" element={<DiaryRedirect />} />
                <Route path="/data" element={<Navigate to="/analytics" replace />} />

                {/* Shared routes */}
                <Route path="/schedules" element={<ProtectedRoute><PageLayout title="weeklySchedules"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><WeeklySchedule /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                {/* /advisor and /irrigation redirect to their intended surfaces */}
                <Route path="/advisor" element={<Navigate to="/" replace />} />
                <Route path="/irrigation" element={<Navigate to="/schedules" replace />} />

                {/* Shared monitoring routes */}
                <Route path="/analytics" element={<ProtectedRoute><PageLayout title="analytics"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><Analytics /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                <Route path="/alerts" element={<ProtectedRoute><PageLayout title="alerts"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><Alerts /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute><PageLayout title="reportsPageTitle"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><Reports /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                {/* The demo. It renders nothing of its own: it starts the
                    guided tour in showcase mode and hands over to the real
                    app, so what a visitor watches is the product itself on
                    real data, not a second TerraMind built to look like it. */}
                <Route path="/showcase" element={<ProtectedRoute><ErrorBoundary><Suspense fallback={<PageSkeleton />}><ShowcaseLauncher /></Suspense></ErrorBoundary></ProtectedRoute>} />

                {/* Admin routes */}
                <Route path="/admin-analytics" element={<ProtectedRoute adminOnly><PageLayout title="systemAnalytics"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><AdminAnalytics /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                <Route path="/users" element={<ProtectedRoute adminOnly><PageLayout title="userManagement"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><UserManagement /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                <Route path="/admin/soil-profiles" element={<ProtectedRoute adminOnly><PageLayout title="soilProfiles"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><SoilProfileEditor /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                <Route path="/admin/history" element={<ProtectedRoute adminOnly><PageLayout title="historyNav"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><HistoryPage /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                {/* Retired standalone hardware pages — now tabs in /devices (Hardware). */}
                <Route path="/sensors" element={<Navigate to="/devices?tab=registry" replace />} />
                <Route path="/edge-devices" element={<Navigate to="/devices?tab=edge" replace />} />
                <Route path="/devices" element={<ProtectedRoute><PageLayout title="hardware"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><Hardware /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                <Route path="/messages" element={<ProtectedRoute adminOnly><PageLayout title="supportInbox"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><SupportMessages /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><PageLayout title="settings"><ErrorBoundary><Suspense fallback={<PageSkeleton />}><Settings /></Suspense></ErrorBoundary></PageLayout></ProtectedRoute>} />
                {/* /dss-demo is an admin technical tool — admin-only guard */}
                <Route path="/dss-demo" element={<ProtectedRoute adminOnly><DssDemoRoute /></ProtectedRoute>} />

                {/* 404 catch-all */}
                <Route path="*" element={<ProtectedRoute><PageLayout title="Not Found"><NotFound /></PageLayout></ProtectedRoute>} />
              </Routes>
            </ErrorBoundary>
            </TourProvider>
          </Router>
        </ThemeProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
